// ============================================
// USER SIDEBAR - Mobil Uyumlu
// ============================================

import { Link, useLocation } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  Award,
  Ship,
  FolderOpen,
  Heart,
  Building2,
  Bell,
  Settings,
  User,
  Users,
  X,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

interface UserSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UserSidebar({ isOpen, onClose }: UserSidebarProps) {
  const { t } = useTranslation('panel');
  const location = useLocation();

  const navigation = [
    { name: t('sidebar.dashboard'), href: '/', icon: LayoutDashboard },
    { name: t('sidebar.jobs'), href: '/jobs', icon: Briefcase },
    { name: t('sidebar.applications'), href: '/applications', icon: FileText },
    { name: t('sidebar.favorites'), href: '/favorites', icon: Heart },
  ];

  const profileNavigation = [
    { name: t('sidebar.myProfile'), href: '/profile', icon: User },
    { name: t('sidebar.certificates'), href: '/profile/certificates', icon: Award },
    { name: t('sidebar.documents'), href: '/profile/documents', icon: FolderOpen },
    { name: t('sidebar.references'), href: '/profile/references', icon: Users },
    { name: t('sidebar.seaService'), href: '/profile/sea-service', icon: Ship },
  ];

  const secondaryNavigation = [
    { name: t('sidebar.companies'), href: '/companies', icon: Building2 },
    { name: t('sidebar.notifications'), href: '/notifications', icon: Bell },
    { name: t('sidebar.settings'), href: '/settings', icon: Settings },
  ];

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  // Mobilde link tıklanınca sidebar'ı kapat
  const handleLinkClick = () => {
    onClose();
  };

  return (
    <>
      {/* Mobil Overlay/Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 md:w-64 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out',
          // Masaüstü: her zaman görünür
          'md:translate-x-0',
          // Mobil: isOpen durumuna göre
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        {/* Logo + Mobil Kapat */}
        <div className="flex items-center justify-between h-16 px-4 md:px-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10">
              <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Crew At Sea</h1>
              <p className="text-xs text-gray-500">{t('sidebar.seafarerPanel')}</p>
            </div>
          </div>
          {/* Mobilde kapat butonu */}
          <button
            onClick={onClose}
            className="p-2 rounded-maritime text-gray-400 hover:text-gray-600 hover:bg-gray-100 md:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Ana Sayfaya Dön */}
        <div className="border-b border-gray-200 p-2">
          <a
            href="https://crewatsea.com"
            className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-maritime transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400" />
            Ana Sayfaya Dön
          </a>
        </div>

        {/* Navigasyon */}
        <nav className="flex flex-col h-[calc(100vh-4rem)] px-3 py-4 overflow-y-auto">
          {/* Ana Navigasyon */}
          <div className="space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={handleLinkClick}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-maritime transition-colors',
                  isActive(item.href)
                    ? 'bg-maritime-50 text-maritime-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                <item.icon
                  className={cn(
                    'w-5 h-5',
                    isActive(item.href) ? 'text-maritime-600' : 'text-gray-400'
                  )}
                />
                {item.name}
              </Link>
            ))}
          </div>

          {/* Profil Bölümü */}
          <div className="mt-6">
            <p className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {t('sidebar.profileSection')}
            </p>
            <div className="space-y-1">
              {profileNavigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={handleLinkClick}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-maritime transition-colors',
                    isActive(item.href)
                      ? 'bg-maritime-50 text-maritime-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <item.icon
                    className={cn(
                      'w-5 h-5',
                      isActive(item.href) ? 'text-maritime-600' : 'text-gray-400'
                    )}
                  />
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Boşluk */}
          <div className="flex-1" />

          {/* Alt Navigasyon */}
          <div className="pt-4 mt-4 border-t border-gray-200 space-y-1">
            {secondaryNavigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={handleLinkClick}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-maritime transition-colors',
                  isActive(item.href)
                    ? 'bg-maritime-50 text-maritime-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                <item.icon
                  className={cn(
                    'w-5 h-5',
                    isActive(item.href) ? 'text-maritime-600' : 'text-gray-400'
                  )}
                />
                {item.name}
              </Link>
            ))}
          </div>
        </nav>
      </aside>
    </>
  );
}
